<script setup lang="ts">
import { defineComponent } from 'vue'
import { definePage } from 'vue-router/auto'
import { useForgotPasswordService } from '~/services/auth/useForgotPasswordService'
import { Card } from '~/components/ui/card'
import Button from '~/components/ui/button/Button.vue'
import Label from '~/components/ui/label/Label.vue'
import Input from '~/components/ui/input/Input.vue'

definePage({
  meta: {
    requiresAuth: false,
    allowAuthenticated: true,
  },
})

defineComponent({
  name: 'ForgotPassword',
})

const { email, errors, onSubmit, isLoading, sentTo, resend, resendError, secondsLeft, countdown } =
  useForgotPasswordService()

// Moves focus to a screen's heading when the step changes, so screen readers announce it.
const vFocus = { mounted: (el: HTMLElement) => el.focus() }
</script>

<template>
  <Card class="w-full max-w-[400px] px-6 pb-6 pt-7 shadow-sm">
    <form v-if="!sentTo" class="flex flex-col gap-6" novalidate @submit="onSubmit">
      <div class="flex flex-col gap-2">
        <span class="text-xs font-semibold uppercase tracking-[0.08em] text-primary">
          {{ $t('Professional portal') }}
        </span>
        <h1 class="font-display text-2xl font-semibold leading-[1.2] tracking-[-0.01em]">
          {{ $t('Forgot password') }}
        </h1>
        <p class="text-sm text-muted-foreground">
          {{
            $t(
              'Enter the email address you sign in with. You will receive a link to set a new password.',
            )
          }}
        </p>
      </div>
      <div class="grid gap-2">
        <Label for="login-email">{{ $t('Email address') }}</Label>
        <Input
          id="login-email"
          v-model="email"
          :error-message="errors.email"
          type="email"
          inputmode="email"
          autocomplete="username"
          name="email"
          autofocus
          :aria-invalid="!!errors.email"
          class="h-10 max-sm:h-11 max-sm:text-base"
        />
      </div>
      <div class="flex flex-col gap-3">
        <Button type="submit" class="h-10 w-full font-semibold max-sm:h-12" :is-loading="isLoading">
          {{ $t('Send link') }}
        </Button>
        <router-link to="/auth/login" class="text-center text-sm underline hover:text-primary">
          {{ $t('Back to sign in') }}
        </router-link>
      </div>
    </form>

    <div v-else class="flex flex-col gap-6">
      <div class="flex flex-col gap-2">
        <span class="text-xs font-semibold uppercase tracking-[0.08em] text-primary">
          {{ $t('Professional portal') }}
        </span>
        <h1
          v-focus
          tabindex="-1"
          class="font-display text-2xl font-semibold leading-[1.2] tracking-[-0.01em] outline-none"
        >
          {{ $t('Check your email') }}
        </h1>
        <p class="text-sm text-muted-foreground">
          {{
            $t(
              'If an account belongs to {email}, you will receive an email with a link within a few minutes. The link is valid for {duration}.',
              { email: sentTo, duration: $t('60 minutes') },
            )
          }}
        </p>
      </div>
      <p class="rounded-md bg-muted px-3 py-2.5 text-[13px] leading-[18px] text-foreground/80">
        <strong class="font-semibold">{{ $t('Tip:') }}</strong>
        {{ $t('No email? Also check your junk mail and whether the email address is correct.') }}
      </p>
      <p v-if="resendError" role="alert" class="text-sm text-red-700 dark:text-red-400">
        {{ resendError }}
      </p>
      <div class="flex flex-col gap-3">
        <Button
          type="button"
          variant="outline"
          class="h-10 w-full font-semibold max-sm:h-12"
          :disabled="secondsLeft > 0"
          :is-loading="isLoading"
          @click="resend"
        >
          {{
            secondsLeft > 0 ? $t('You can resend in {time}', { time: countdown }) : $t('Send again')
          }}
        </Button>
        <router-link to="/auth/login" class="text-center text-sm underline hover:text-primary">
          {{ $t('Back to sign in') }}
        </router-link>
      </div>
    </div>
  </Card>
</template>
